import JSZip from 'jszip';

/**
 * Fills Eric's blank VASI Word report (public/report-templates/) with an
 * edited cloud report. Only word/document.xml is rewritten, so his
 * letterhead, header/footer, images, disclaimers and signature are kept.
 * Each value reuses the paragraph and run formatting already in his file.
 */

export const DOCX_TEMPLATE_URL = `${process.env.PUBLIC_URL ?? ''}/report-templates/vasi-male-report.docx`;
export const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const GROUP_HEADINGS = [
  ['FA', 'Female Adults'],
  ['MA', 'Male Adults'],
  ['FAD', 'Female Adolescents'],
  ['MAD', 'Male Adolescents'],
  ['FC', 'Female Children'],
  ['MC', 'Male Children'],
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const NAME_SUFFIXES = new Set(['JR', 'SR', 'II', 'III', 'IV', 'V']);

export function escapeXml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function unescapeXml(text) {
  return String(text)
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}

/** "2026-11-03" -> "November 3, 2026"; anything else is used as typed. */
export function formatAssessmentDate(value) {
  const match = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(value ?? '');
  const month = MONTHS[Number(match[2]) - 1];
  if (!month) return String(value);
  return `${month} ${Number(match[3])}, ${match[1]}`;
}

export function lastNameOf(clientName) {
  const parts = String(clientName ?? '').trim().split(/\s+/).filter(Boolean);
  while (parts.length > 1 && NAME_SUFFIXES.has(parts[parts.length - 1].replace(/[.,]/g, '').toUpperCase())) {
    parts.pop();
  }
  return (parts[parts.length - 1] ?? '').replace(/,$/, '');
}

function fileSafe(value) {
  return String(value ?? '').replace(/[^A-Za-z0-9-]+/g, '');
}

export function docxFileName({ caseNumber, clientName } = {}) {
  const parts = ['VASI', fileSafe(caseNumber), fileSafe(lastNameOf(clientName))].filter(Boolean);
  return `${parts.join('_')}.docx`;
}

// --- document.xml helpers --------------------------------------------------

const PARAGRAPH_RE = /<w:p(?=[\s>/])[^>]*?(?:\/>|>[\s\S]*?<\/w:p>)/g;
const RUN_RE = /<w:r(?=[\s>])[^>]*>[\s\S]*?<\/w:r>/g;

function tokenize(xml) {
  const tokens = [];
  let last = 0;
  xml.replace(PARAGRAPH_RE, (match, offset) => {
    if (offset > last) tokens.push({ p: false, xml: xml.slice(last, offset) });
    tokens.push({ p: true, xml: match });
    last = offset + match.length;
    return match;
  });
  if (last < xml.length) tokens.push({ p: false, xml: xml.slice(last) });
  return tokens;
}

export function paragraphText(paragraphXml) {
  const parts = [];
  paragraphXml.replace(/<w:t(?:\s[^>]*)?>([\s\S]*?)<\/w:t>/g, (match, text) => {
    parts.push(unescapeXml(text));
    return match;
  });
  return parts.join('');
}

function normalizeText(text) {
  return String(text ?? '').replace(/\s+/g, ' ').trim();
}

function openTag(paragraphXml) {
  const tag = paragraphXml.match(/^<w:p(?=[\s>/])[^>]*?\/?>/)[0];
  return tag.endsWith('/>') ? `${tag.slice(0, -2)}>` : tag;
}

function paragraphProps(paragraphXml) {
  return paragraphXml.match(/<w:pPr>[\s\S]*?<\/w:pPr>/)?.[0] ?? '';
}

function runProps(runXml) {
  return runXml?.match(/<w:rPr>[\s\S]*?<\/w:rPr>/)?.[0] ?? '';
}

/** A copied paragraph must not reuse Word's paragraph ids. */
function cloneOpenTag(paragraphXml) {
  return openTag(paragraphXml).replace(/\s+w14:(?:paraId|textId)="[^"]*"/g, '');
}

function runsXml(text, rPr = '') {
  const lines = String(text ?? '').split('\n');
  const content = lines
    .map((line) => `<w:t xml:space="preserve">${escapeXml(line)}</w:t>`)
    .join('<w:br/>');
  return `<w:r>${rPr}${content}</w:r>`;
}

function splitParagraphs(text) {
  return String(text ?? '')
    .replace(/\r\n?/g, '\n')
    .split(/\n[ \t]*\n/)
    .map((part) => part.replace(/^\n+|\n+$/g, ''))
    .filter((part) => part.trim());
}

/** Append text to a paragraph (e.g. an empty one under a heading). */
function appendToParagraph(paragraphXml, runs) {
  if (/\/>$/.test(paragraphXml) && !paragraphXml.includes('</w:p>')) {
    return `${openTag(paragraphXml)}${runs}</w:p>`;
  }
  return paragraphXml.replace(/<\/w:p>$/, `${runs}</w:p>`);
}

/** Replace every run in a paragraph with new text, keeping its pPr. */
function replaceParagraphText(paragraphXml, text, rPr) {
  const firstRun = paragraphXml.match(RUN_RE)?.[0];
  return `${openTag(paragraphXml)}${paragraphProps(paragraphXml)}${runsXml(text, rPr ?? runProps(firstRun))}</w:p>`;
}

/**
 * "Client Name: <tab><tab>" style lines: keep the label and its tabs, drop
 * whatever value followed (e.g. the provider name in the blank), append ours.
 */
function setLabelValue(paragraphXml, value) {
  const runs = [];
  paragraphXml.replace(RUN_RE, (match, offset) => {
    runs.push({ xml: match, end: offset + match.length });
    return match;
  });
  if (runs.length === 0) return paragraphXml;
  const tabRuns = runs.filter((run) => run.xml.includes('<w:tab/>'));
  const anchor = tabRuns.length ? tabRuns[tabRuns.length - 1] : runs[0];
  const text = String(value ?? '').trim();
  const valueRun = text ? runsXml(text, runProps(runs[0].xml)) : '';
  return `${paragraphXml.slice(0, anchor.end)}${valueRun}</w:p>`;
}

function newParagraphsLike(templateParagraph, texts) {
  const tag = cloneOpenTag(templateParagraph);
  const pPr = paragraphProps(templateParagraph);
  return texts.map((text) => `${tag}${pPr}${runsXml(text)}</w:p>`).join('');
}

function closingIsDefault(closing, defaults) {
  const normalize = (text) => normalizeText(text)
    .replace(/suggest a \S+ risk/g, 'suggest a [] risk')
    .toLowerCase();
  const value = normalize(closing);
  if (!value) return true;
  return defaults.some((text) => normalize(text) === value);
}

/**
 * Fill document.xml from an edited report:
 *   header, intro, caution (before Female Adults), the six group paragraphs,
 *   summary, the "suggest a [] risk" word, and the closing (only when it was
 *   changed from Eric's own wording).
 */
export function fillReportDocumentXml(xml, report, { sex } = {}) {
  const tokens = tokenize(xml);
  const paragraphIndexes = () => tokens.map((token, index) => (token.p ? index : -1)).filter((index) => index >= 0);
  const findParagraph = (predicate) => paragraphIndexes().find((index) => predicate(paragraphText(tokens[index].xml)));
  const nextParagraph = (index) => paragraphIndexes().find((candidate) => candidate > index);

  const header = report?.header ?? {};
  const sections = new Map((report?.sections ?? []).map((section) => [section.id, section]));

  [
    ['Client Name:', header.clientName],
    ['Provider:', header.provider],
    ['Date of Assessment:', formatAssessmentDate(header.assessmentDate)],
  ].forEach(([label, value]) => {
    const index = findParagraph((text) => text.trim().startsWith(label));
    if (index !== undefined) tokens[index].xml = setLabelValue(tokens[index].xml, value);
  });

  const introIndex = findParagraph((text) => text.includes('-year-old'));
  if (introIndex !== undefined && normalizeText(report?.intro)) {
    tokens[introIndex].xml = replaceParagraphText(tokens[introIndex].xml, normalizeText(report.intro));
  }

  // Group paragraphs go in the first empty paragraph under each heading.
  let cautionAnchor;
  GROUP_HEADINGS.forEach(([id, heading]) => {
    const headingIndex = findParagraph((text) => normalizeText(text) === heading);
    if (headingIndex === undefined) return;
    if (id === 'FA') cautionAnchor = headingIndex;
    const bodyIndex = nextParagraph(headingIndex);
    if (bodyIndex === undefined) return;
    const parts = splitParagraphs(sections.get(id)?.text);
    if (parts.length === 0) return;
    const body = tokens[bodyIndex].xml;
    tokens[bodyIndex].xml = appendToParagraph(body, runsXml(parts[0]))
      + newParagraphsLike(body, parts.slice(1));
  });

  const cautionParts = splitParagraphs(sections.get('caution')?.text);
  if (cautionAnchor !== undefined && cautionParts.length > 0) {
    const like = tokens[nextParagraph(cautionAnchor)]?.xml ?? tokens[cautionAnchor].xml;
    const spacer = `${cloneOpenTag(like)}${paragraphProps(like)}</w:p>`;
    tokens[cautionAnchor].xml = `${newParagraphsLike(like, cautionParts)}${spacer}${tokens[cautionAnchor].xml}`;
  }

  const summaryHeading = findParagraph((text) => normalizeText(text) === 'Summary');
  const summaryParts = splitParagraphs(report?.summary?.text);
  if (summaryHeading !== undefined && summaryParts.length > 0) {
    const bodyIndex = nextParagraph(summaryHeading);
    if (bodyIndex !== undefined) {
      const body = tokens[bodyIndex].xml;
      tokens[bodyIndex].xml = appendToParagraph(body, runsXml(summaryParts[0]))
        + newParagraphsLike(body, summaryParts.slice(1));
    }
  }

  const riskIndex = findParagraph((text) => text.includes('risk of recidivism') && text.includes('[]'));
  const contactIndex = findParagraph((text) => normalizeText(text).startsWith('Sexual interest is one risk factor'));
  const risk = report?.risk ? String(report.risk) : null;
  if (riskIndex !== undefined) {
    const defaults = [paragraphText(tokens[riskIndex].xml)];
    if (contactIndex !== undefined) defaults.push(paragraphText(tokens[contactIndex].xml));
    if (closingIsDefault(report?.closing, defaults)) {
      if (risk) {
        tokens[riskIndex].xml = tokens[riskIndex].xml.replace(
          /(<w:t(?:\s[^>]*)?>)\[\](<\/w:t>)/,
          `$1${escapeXml(risk)}$2`
        );
      }
    } else {
      const closing = risk ? report.closing.replace(/\[\]/g, risk) : report.closing;
      const parts = splitParagraphs(closing);
      const original = tokens[riskIndex].xml;
      tokens[riskIndex].xml = replaceParagraphText(original, parts[0] ?? '', '')
        + newParagraphsLike(original, parts.slice(1));
    }
  }

  let filled = tokens.map((token) => token.xml).join('');
  if (sex === 'female') {
    filled = filled.replace(/(<w:t(?:\s[^>]*)?>[^<]*?)\bin his treatment\b/g, '$1in her treatment');
  }
  return filled;
}

export async function buildReportDocx(templateData, report, { sex, type = 'blob' } = {}) {
  const zip = await JSZip.loadAsync(templateData);
  const documentFile = zip.file('word/document.xml');
  if (!documentFile) throw new Error('The Word template has no word/document.xml.');
  const xml = await documentFile.async('string');
  zip.file('word/document.xml', fillReportDocumentXml(xml, report, { sex }), { createFolders: false });
  return zip.generateAsync({ type, mimeType: DOCX_MIME, compression: 'DEFLATE' });
}

export async function fetchDocxTemplate(url = DOCX_TEMPLATE_URL, fetchImpl = fetch) {
  const response = await fetchImpl(url);
  if (!response.ok) throw new Error(`Could not load the Word template (${response.status}).`);
  return response.arrayBuffer();
}
